'use client';

/**
 * * PageHeader
 *
 * * Layout-only header component intended to be rendered by
 * * section-level Next.js layouts (e.g. /me/layout.tsx).
 *
 * * This component does not own business state.
 * * Controls rendered inside it should read/write shared state
 * * via page- or section-scoped context when persistence across
 * * sibling routes is required.
 */

import React from 'react';
import { IoVideocam } from 'react-icons/io5';

import {
  BackButton,
  CopyLinkIcon,
  Divider,
  SecondaryButton,
} from '../../atomic-atoms';

import styles from './SectionPageHeader.module.scss';

export type SectionPageHeaderProps = {
  title: string;
  showBackButton?: boolean;
  onBackButtonClick?: () => void;
  onHelpButtonClick?: () => void;
  controlsTop?: React.ReactNode;
  controlsLeft?: React.ReactNode;
  controlsCenter?: React.ReactNode;
  controlsRight?: React.ReactNode;
};

export function SectionPageHeader({
  title,
  showBackButton = false,
  onBackButtonClick,
  onHelpButtonClick,
  controlsTop,
  controlsLeft,
  controlsCenter,
  controlsRight,
}: SectionPageHeaderProps) {
  return (
    <header className={styles.pageHeader}>
      <div className={styles.titleRow}>
        {showBackButton ? <BackButton onClick={onBackButtonClick} /> : null}

        {/* // * Remove until Net Net AI is enabled */}
        {/* <SecondaryButton
          ariaLabel="Open help video drawer"
          variant="mini"
          onClick={onHelpButtonClick}
        >
          <IoVideocam />
        </SecondaryButton> */}

        <div className={styles.titleWrapper}>
          <h1 className={styles.title}>{title}</h1>
          <span className={styles.titleLinkIcon}>
            <CopyLinkIcon ariaLabel="Copy page link" />
          </span>
        </div>
      </div>

      {controlsTop && (
        <>
          <div className={styles.controlsTopRow}>{controlsTop}</div>
          <Divider />
        </>
      )}

      {(controlsLeft || controlsCenter || controlsRight) && (
        <div className={styles.controlsRow}>
          <div className={styles.controlsLeft}>{controlsLeft}</div>

          {controlsCenter && (
            <div className={styles.controlsCenter}>{controlsCenter}</div>
          )}

          <div className={styles.controlsRight}>{controlsRight}</div>
        </div>
      )}
    </header>
  );
}

// * Example usage:

/* <PageHeader
  title="My Contacts"
  onHelpButtonClick={() => {
    // * Here we trigger the help video drawer passing the page slug or something.
  }}
  controlsLeft={
    <>
      <SecondaryButton
        ariaLabel="Open contacts settings"
        variant="medium"
        onClick={() => {}}
      >
        <RiContactsBook3Fill />
      </SecondaryButton>

      <SecondaryButton
        ariaLabel="Open contacts members"
        variant="medium"
        onClick={() => {}}
      >
        <RiContactsBook2Fill />
      </SecondaryButton>
    </>
  }
  controlsCenter={
    <SearchBar placeholder="Search" value="" onChange={() => {}} />
  }
  controlsRight={
    <SecondaryButton
      ariaLabel="Open contacts trust"
      variant="mini"
      onClick={() => {}}
    >
      <RiContactsFill />
    </SecondaryButton>
  }
/> */
